import { useState, useCallback, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useMyServices, useTechRadar } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useWidgetPolling } from './useWidgetPolling';
import { TechRadarChart, RING_ORDER, RING_COLORS } from '../technology/TechRadarChart';
import type { TechRadarBlip } from '../../graphql/types';
import type { WidgetConfig, WidgetState } from './types';
import '@tylertech/forge/icon';
import '@tylertech/forge/button';
import './TechnologyRadarWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'technology-radar',
  title: 'My Services Tech Radar',
  size: 'large',
  refreshInterval: 60000,
  showRefresh: true,
  showExpand: false,
};

const RADAR_SIZE = 300;

export function TechnologyRadarWidget(): JSX.Element {
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [selected, setSelected] = useState<TechRadarBlip | null>(null);

  const {
    data: servicesData,
    loading: servicesLoading,
    error: servicesError,
    refetch: refetchServices,
  } = useMyServices(1000);

  const serviceIds = useMemo(
    () => servicesData?.myServices?.edges?.map((e) => e.node.entityId) ?? [],
    [servicesData]
  );

  // Skip the radar query until services have loaded so we don't flash empty state.
  const skipRadar = servicesLoading || !!servicesError;

  const {
    data: radarData,
    loading: radarLoading,
    error: radarError,
    refetch: refetchRadar,
  } = useTechRadar({ serviceIds }, skipRadar);

  const loading = servicesLoading || radarLoading;
  const error = servicesError || radarError;

  const sectors = useMemo(() => radarData?.techRadar?.sectors ?? [], [radarData]);
  const blips = useMemo(() => radarData?.techRadar?.blips ?? [], [radarData]);
  const matchedIds = useMemo(() => new Set(blips.map((b) => b.id)), [blips]);

  const widgetState: WidgetState<unknown> = {
    data: radarData ?? null,
    loading,
    error: error?.message ?? null,
    lastRefresh,
  };

  const handleRefresh = useCallback(() => {
    refetchServices();
    if (!skipRadar) refetchRadar();
    setLastRefresh(new Date());
  }, [refetchServices, refetchRadar, skipRadar]);

  useWidgetPolling({
    interval: WIDGET_CONFIG.refreshInterval!,
    onRefresh: handleRefresh,
    enabled: !loading && !error,
  });

  if (radarData && !lastRefresh) {
    setLastRefresh(new Date());
  }

  const IconElement = 'forge-icon' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;
  const noServices = !servicesLoading && !servicesError && serviceIds.length === 0;
  const noTech = !loading && !error && blips.length === 0 && !noServices;

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      {noServices ? (
        <div className="tech-radar-widget__empty">
          <IconElement name="cloud_off" className="tech-radar-widget__empty-icon" />
          <p className="forge-typography--body2">No services in My Services</p>
          <p className="forge-typography--caption">
            Create a service or join a team to see its tech radar.
          </p>
        </div>
      ) : noTech ? (
        <div className="tech-radar-widget__empty">
          <IconElement name="chart_arc" className="tech-radar-widget__empty-icon" />
          <p className="forge-typography--body2">No technologies mapped</p>
          <p className="forge-typography--caption">
            Link technologies to your services via USES_TECH relationships.
          </p>
        </div>
      ) : (
        <div className="tech-radar-widget__content">
          <div className="tech-radar-widget__chart">
            <TechRadarChart
              sectors={sectors}
              blips={blips}
              matchedIds={matchedIds}
              filtersActive={false}
              selectedId={selected?.id ?? null}
              onSelect={setSelected}
              size={RADAR_SIZE}
            />
          </div>

          <div className="tech-radar-widget__side">
            {selected ? (
              <div className="tech-radar-widget__detail">
                <div className="tech-radar-widget__detail-header">
                  <span className="tech-radar-widget__detail-title">
                    <span
                      className="tech-radar-widget__ring-dot"
                      style={{ backgroundColor: RING_COLORS[selected.ring] }}
                      aria-hidden="true"
                    />
                    <span className="forge-typography--subtitle2 tech-radar-widget__detail-name">
                      {selected.name}
                    </span>
                  </span>
                  <ForgeButton
                    type="button"
                    variant="text"
                    aria-label="Clear selection"
                    onClick={() => setSelected(null)}
                  >
                    <IconElement name="close" aria-hidden="true" />
                  </ForgeButton>
                </div>

                <dl className="tech-radar-widget__detail-dl forge-typography--caption">
                  <dt>Ring</dt>
                  <dd>
                    {selected.ring}
                    {selected.isNew && (
                      <span className="tech-radar-widget__detail-new"> · New</span>
                    )}
                  </dd>
                  <dt>Family</dt>
                  <dd>{selected.family}</dd>
                  <dt>Supplier</dt>
                  <dd>{selected.supplier ?? '—'}</dd>
                  {selected.description && (
                    <>
                      <dt>Description</dt>
                      <dd className="tech-radar-widget__detail-desc">{selected.description}</dd>
                    </>
                  )}
                </dl>

                <Link
                  to="/technologies/$technologyId"
                  params={{ technologyId: selected.id }}
                  className="tech-radar-widget__link forge-typography--caption"
                >
                  View technology details →
                </Link>
              </div>
            ) : (
              <div className="tech-radar-widget__meta">
                <span className="forge-typography--caption tech-radar-widget__count">
                  {blips.length} {blips.length === 1 ? 'technology' : 'technologies'} across{' '}
                  {serviceIds.length} {serviceIds.length === 1 ? 'service' : 'services'}
                </span>

                <div className="tech-radar-widget__legend">
                  {RING_ORDER.map((ring) => (
                    <span key={ring} className="tech-radar-widget__legend-item">
                      <span
                        className="tech-radar-widget__legend-dot"
                        // ring is from RING_ORDER (TechnologyLifecycleStage enum) — not user-controlled
                        // eslint-disable-next-line security/detect-object-injection
                        style={{ backgroundColor: RING_COLORS[ring] }}
                        aria-hidden="true"
                      />
                      <span className="forge-typography--caption">{ring}</span>
                    </span>
                  ))}
                </div>

                <span className="forge-typography--caption tech-radar-widget__hint">
                  Click a blip for product details.
                </span>

                <Link
                  to="/technologies/radar"
                  className="tech-radar-widget__link forge-typography--caption"
                >
                  Open full radar →
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardWidget>
  );
}
